// react
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// redux
import { useDispatch, useSelector } from "react-redux";
import { logoutUser, resetAllData } from "../../redux/actions/userAction";

// interfaces
import { AppState } from "../../redux/types";

import Avatar from "./Avatar";
import Sidebar from "./Sidebar";

// components

// feather icons
import moment from "moment";
import {
  ArrowRight,
  Clock,
  LogOut,
  MapPin,
  Package,
  Search,
  User,
  UserCheck,
  UserX,
  X
} from "react-feather";
import {
  getActivity,
  setUserCheckIn,
  setUserCheckOut,
  userCheckIn,
  userCheckOut,
} from "../../redux/actions/hr/hrActions";
import { getAWBIdByAWBNumber } from "../../redux/sagas/requests/operations/awbRequests";
import { apiRequestHandler } from "../../utils/commonFunctions";
import NewConfirmationModal from "../modal/NewConfirmationModal";
import LocationModalContent from "./LocationModalContent";
import { Menu as MuiMenu, MenuItem } from "@mui/material";
import { ChevronDown, Zap } from "react-feather";
import { PlusCircle, Upload, Calendar, HelpCircle } from "react-feather";

const quickActions = [
  { to: "/operations/awb/create", label: "Create AWB", description: "Book a new shipment", icon: PlusCircle, tone: "bg-[#E1383B]/10 text-[#E1383B]" },
  { to: "/operations/awb/import", label: "Import AWB", description: "Upload AWBs in bulk", icon: Upload, tone: "bg-[#EF7F1A]/10 text-[#EF7F1A]" },
  { to: "/operations/appointment/create", label: "Create Appointment", description: "Schedule a delivery slot", icon: Calendar, tone: "bg-[#009846]/10 text-[#009846]" },
  { to: "/operations/request Support", label: "Request Support", description: "Raise a support request", icon: HelpCircle, tone: "bg-[#5B5B5B]/10 text-[#5B5B5B]" },
];

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isCheckIn = useSelector((state: AppState) => state?.hr?.attendance?.isCheckedIn); //true or false
  const isCheckedOut = useSelector((state: AppState) => state?.hr?.attendance?.isCheckedOut); //true or false
  const activityData = useSelector((state: AppState) => state?.hr?.hrActivity?.activityData);
  const [activity, setActivity] = useState(activityData);
  const [totalWorkDuration, setTotalWorkDuration] = useState<string | null>(null);
  const [quickActionAnchor, setQuickActionAnchor] = useState<null | HTMLElement>(null);

  const openQuickAction = Boolean(quickActionAnchor);

  const handleQuickActionOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setQuickActionAnchor(e.currentTarget);
  };

  const handleQuickActionClose = () => {
    setQuickActionAnchor(null);
  };

  const awbInputRef = useRef<HTMLInputElement>(null);
  const [isSearching, setIsSearching] = useState(false);

  // "/" jumps to the AWB search from anywhere outside a text field
  useEffect(() => {
    const handleSlash = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
      if (e.key === "/" && !isTyping) {
        e.preventDefault();
        awbInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleSlash);
    return () => document.removeEventListener("keydown", handleSlash);
  }, []);
  useEffect(() => {
    setActivity(activityData);
  }, [activityData]);

  useEffect(() => {
    dispatch(getActivity({ date: moment(new Date()).format("YYYY-MM-DD") }));
  }, [dispatch]);

  const refereshCheckInStatus = useCallback(() => {
    if (activity.check_in) {
      dispatch(setUserCheckIn(true));
    } else if (activity.check_out) {
      dispatch(setUserCheckOut(true));
    }
  }, [activity.check_in, activity.check_out, dispatch]);

  const startTimer = useCallback(() => {
    if (activity != null && activity.check_in != null) {
      if (activity.check_in != null && activity.check_out != null) {
        setTotalWorkDuration(activity.total_work_duration);
      } else {
        // alreadyCheckedIn = true;
        // checkInTime = activity.check_in;
        // setState(() {
        //   loading = false;
        //   alreadyCheckInTime = checkInTime;
        //   startPeriodTimer();
        // });
      }
    } else {
      // setState(() {
      //   loading = false;
      // });
    }
  }, [activity]);

  useEffect(() => {
    refereshCheckInStatus();
    startTimer();
  }, [activity, refereshCheckInStatus, startTimer]);

  const [isProfileOpen, setIsProfileOpen] = useState<boolean>(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // close the profile menu on outside click or Escape
  useEffect(() => {
    if (!isProfileOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setIsProfileOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProfileOpen]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [openLocationModal, setOpenLocationModal] = useState(false);
  const name = useSelector((state: AppState) => state?.user?.myProfile?.name ?? "--");
  const role = useSelector((state: AppState) => state?.user?.myProfile?.role.role_name ?? "--");
  const assignedLocation = useSelector((state: AppState) => state.user.loggedInUserData.office_assigned_data);
  const userOfficeLocationData = useSelector(
    (state: AppState) => state.user.loggedInUserData.user_office_location_data
  );

  if (!localStorage.getItem("office-location-id")) {
    localStorage.setItem("office-location-id", String(userOfficeLocationData.id));
  }

  let officeLocationId = localStorage.getItem("office-location-id") || "0";

  const currentLocation = assignedLocation?.find((location) => location.value === parseInt(officeLocationId));

  const attendanceStatus = isCheckIn
    ? { label: "Checked in", className: "bg-[#009846]/10 text-[#009846] ring-[#009846]/30", dot: "bg-[#009846]", text: "text-[#009846]" }
    : isCheckedOut
      ? { label: "Checked out", className: "bg-[#EF7F1A]/10 text-[#EF7F1A] ring-[#EF7F1A]/30", dot: "bg-[#EF7F1A]", text: "text-[#EF7F1A]" }
      : null;

  const siteConfigData = useSelector((state: AppState) => state.siteThemeConfig);

  // const setThemeRootColors = () => {
  //   const rootColorsStyleTag = document.querySelectorAll("[data-theme='root-colors']");
  //   rootColorsStyleTag.forEach((styleTag) => {
  //     console.log("inside style tag", styleTag)
  //     styleTag.innerHTML = `
  //         :root {
  //           --primary-default: #08A6A1;
  //           --primary-dark: #055653;
  //           --primary-medium: #077e7b;
  //           --primary-light: #08a6a133;
  //         }
  //       `;
  //   });
  // };

  // logout user function
  const handleLogout = () => {
    localStorage.clear();
    dispatch(resetAllData());
    dispatch(logoutUser());

    // resetting the default theme
    // setThemeRootColors();

    // closing the profile option button
    setIsProfileOpen(false);
  };

  const handleCheckin = () => {
    dispatch(
      userCheckIn({
        geo_location: "https://google.com",
        device_info: "all the device info will be save here",
      })
    );
  };

  const handleCheckout = () => {
    dispatch(
      userCheckOut({
        geo_location: "https://google.com",
        device_info: "all the device info will be save here",
      })
    );
  };

  const handleAWBSearch = async (input: string) => {
    const query = input.trim();
    if (!query || isSearching) return;
    setIsSearching(true);
    try {
      const response = await apiRequestHandler(getAWBIdByAWBNumber, query);
      const payload = response?.data;
      if (payload && payload.id) {
        setSearchInput("");
        awbInputRef.current?.blur();
        navigate(`/operations/awb/${payload.id}`);
      }
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <>
      <div className="sticky left-0 right-0 top-0 z-[999] h-[68px] w-full border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-md">
        <header className="mx-auto flex h-full max-w-7xl items-center gap-4">
          {/* logo */}
          <Link to="/dashboard" className="flex shrink-0 items-center">
            <img src={siteConfigData.logo_img} alt="logo" className="h-12 w-auto object-contain" />
          </Link>

          {/* AWB search */}
          <form
            role="search"
            className="group relative ml-6 hidden h-11 w-full max-w-md items-center rounded-full border-[1.5px] border-slate-300 bg-slate-50 p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] transition-all duration-300 hover:border-slate-400 focus-within:max-w-xl focus-within:border-[#E1383B] focus-within:bg-white focus-within:shadow-none focus-within:ring-4 focus-within:ring-[#E1383B]/15 md:flex"
            onSubmit={(e) => {
              e.preventDefault();
              handleAWBSearch(searchInput);
            }}
          >
            <span className="flex h-full shrink-0 items-center gap-1.5 rounded-full bg-white px-2.5 text-[11px] font-bold uppercase tracking-wider text-[#5B5B5B] shadow-sm ring-1 ring-slate-200 transition group-focus-within:bg-[#E1383B] group-focus-within:text-white group-focus-within:ring-[#E1383B]">
              <Package size={13} />
              AWB
            </span>
            <input
              ref={awbInputRef}
              type="text"
              value={searchInput}
              placeholder="Track by AWB or forwarding no."
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setSearchInput("");
                  e.currentTarget.blur();
                }
              }}
              aria-label="Search AWB or forwarding number"
              className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm font-medium tracking-wide text-slate-800 outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400 focus:ring-0"
            />
            {searchInput ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    awbInputRef.current?.focus();
                  }}
                  className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
                <button
                  type="submit"
                  disabled={isSearching}
                  className="flex h-full aspect-square shrink-0 items-center justify-center rounded-full bg-[#E1383B] text-white shadow-sm transition hover:bg-[#C42B2E] disabled:opacity-70"
                  aria-label="Search"
                >
                  {isSearching ? (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  ) : (
                    <ArrowRight size={15} />
                  )}
                </button>
              </>
            ) : (
              <span className="mr-1.5 flex shrink-0 items-center gap-1.5 text-slate-400 group-focus-within:hidden">
                <Search size={14} />
                <kbd className="rounded-md border border-slate-200 bg-white px-1.5 font-sans text-[10px] font-semibold leading-4 text-slate-500 shadow-[0_1px_0_#e2e8f0]">
                  /
                </kbd>
              </span>
            )}
          </form>

          <div className="ml-auto flex items-center gap-3">
            {/* quick actions */}
            <button
              type="button"
              onClick={handleQuickActionOpen}
              className="flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-[#E1383B] to-[#EF7F1A] px-4 text-sm font-semibold text-white shadow-md shadow-[#E1383B]/25 transition hover:-translate-y-px hover:shadow-lg hover:shadow-[#E1383B]/30 hover:brightness-105 active:translate-y-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#E1383B]/20"
            >
              <Zap size={17} className="fill-white text-white" />
              <span className="hidden lg:inline">Quick Actions</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${openQuickAction ? "rotate-180" : ""}`}
              />
            </button>

            <MuiMenu
              anchorEl={quickActionAnchor}
              open={openQuickAction}
              onClose={handleQuickActionClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1,
                  minWidth: 240,
                  borderRadius: "14px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 12px 32px -8px rgba(15, 23, 42, 0.18)",
                },
              }}
              MenuListProps={{ sx: { p: 0.75 } }}
            >
              {quickActions.map(({ to, label, description, icon: Icon, tone }) => (
                <MenuItem
                  key={to}
                  component={Link}
                  to={to}
                  onClick={handleQuickActionClose}
                  sx={{ borderRadius: "10px", gap: 1.5, py: 1 }}
                >
                  <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
                    <Icon size={16} />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-slate-800">{label}</span>
                    <span className="text-xs text-slate-400">{description}</span>
                  </span>
                </MenuItem>
              ))}
            </MuiMenu>

            {/* attendance status */}
            {attendanceStatus && (
              <span
                className={`hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset xl:flex ${attendanceStatus.className}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${attendanceStatus.dot}`} />
                {attendanceStatus.label}
              </span>
            )}

            <span className="hidden h-6 w-px bg-slate-200 sm:block" />

            {/* profile */}
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => setIsProfileOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
                className={`flex items-center gap-2.5 rounded-full border py-1 pl-1 pr-3 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#E1383B]/15 ${isProfileOpen ? "border-[#E1383B]/40 bg-[#E1383B]/5" : "border-slate-200 bg-white"}`}
              >
                <Avatar />
                <span className="hidden max-w-[10rem] flex-col items-start text-left md:flex">
                  <span className="w-full truncate text-sm font-semibold leading-tight text-slate-800">{name}</span>
                  <span className="w-full truncate text-xs leading-tight text-slate-500">{role}</span>
                </span>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${isProfileOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isProfileOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-72 origin-top-right animate-slide-down overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_32px_-8px_rgba(15,23,42,0.18)]"
                >
                  {/* user card */}
                  <div className="flex items-center gap-3 bg-gradient-to-br from-[#E1383B]/10 to-white px-4 py-4">
                    <Avatar size="lg" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">{name}</p>
                      <p className="truncate text-xs text-slate-500">{role}</p>
                    </div>
                  </div>

                  {/* location */}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setIsProfileOpen(false);
                      setOpenLocationModal(true);
                    }}
                    className="flex w-full items-center gap-3 border-y border-slate-100 px-4 py-2.5 text-left transition hover:bg-slate-50"
                  >
                    <MapPin size={16} className="shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] uppercase tracking-wide text-slate-400">Office location</span>
                      <span className="block truncate text-sm font-medium text-slate-700">
                        {currentLocation?.label ?? "Not set"}
                      </span>
                    </span>
                    <span className="text-xs font-semibold text-[#E1383B]">Change</span>
                  </button>

                  {/* attendance */}
                  <div className="px-4 py-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wide text-slate-400">Attendance</span>
                      {totalWorkDuration ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-slate-600">
                          <Clock size={12} /> {totalWorkDuration}
                        </span>
                      ) : attendanceStatus ? (
                        <span className={`text-xs font-semibold ${attendanceStatus.text}`}>{attendanceStatus.label}</span>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          handleCheckin();
                          setIsProfileOpen(false);
                        }}
                        className="flex items-center justify-center gap-1.5 rounded-lg border border-[#009846]/30 bg-[#009846]/10 py-2 text-xs font-semibold text-[#009846] transition hover:bg-[#009846]/20"
                      >
                        <UserCheck size={14} /> Check In
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          handleCheckout();
                          setIsProfileOpen(false);
                        }}
                        className="flex items-center justify-center gap-1.5 rounded-lg border border-[#EF7F1A]/30 bg-[#EF7F1A]/10 py-2 text-xs font-semibold text-[#EF7F1A] transition hover:bg-[#EF7F1A]/20"
                      >
                        <UserX size={14} /> Check Out
                      </button>
                    </div>
                  </div>

                  {/* links */}
                  <div className="border-t border-slate-100 p-1.5">
                    <Link
                      to="/user-me"
                      role="menuitem"
                      onClick={() => setIsProfileOpen(false)}
                      className="flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                    >
                      <User size={16} className="text-slate-400" />
                      My Profile
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-[#E1383B] transition hover:bg-[#E1383B]/10"
                    >
                      <LogOut size={16} />
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-[linear-gradient(90deg,#E1383B_0%,#E1383B_70%,#EF7F1A_70%,#EF7F1A_80%,#ffffff_80%,#ffffff_90%,#009846_90%,#009846_100%)]"
        />
      </div>
      <Sidebar />
      <NewConfirmationModal
        isOpen={openLocationModal}
        onClose={() => setOpenLocationModal(false)}
        onConfirm={() => setOpenLocationModal(false)}
        title="Change User Location"
      >
        <LocationModalContent />
      </NewConfirmationModal>
    </>
  );
};

export default Navbar;
